import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from 'src/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { ConfigurationCriteriaEnum } from 'src/features/configuration/configurationCriteria/enums/ConfigurationCriteriaEnum';
import type { OpenSessionCriterion } from 'src/services/api/getOpenSessionCriteria';

type ConfigurationCriterionChildrenConfigProps = {
  criterion: OpenSessionCriterion;
  criterionKey: ConfigurationCriteriaEnum;
  onUpdateCriteria: (key: string, value: string, number: boolean) => void;
};

export const ConfigurationCriterionChildrenConfig = ({
  criterion,
  criterionKey,
  onUpdateCriteria,
}: ConfigurationCriterionChildrenConfigProps) => {
  const { t } = useTranslation('configuration');
  const [value3, setValue3] = useState(criterion.values?.value3);
  const [value1, setValue1] = useState(criterion.values?.value1);
  const [value2, setValue2] = useState(criterion.values?.value2);

  useEffect(() => {
    setValue3(criterion.values?.value3);
  }, [criterion.values?.value3]);
  useEffect(() => {
    setValue1(criterion.values?.value1);
  }, [criterion.values?.value1]);
  useEffect(() => {
    setValue2(criterion.values?.value2);
  }, [criterion.values?.value2]);

  switch (criterionKey) {
    case ConfigurationCriteriaEnum.ForecastNotValidatedSince:
      return (
        <>
          <span>{t('The forecast has not been validated since')}</span>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue1(e.target.value);
              onUpdateCriteria('value1', e.target.value, true);
            }}
            value={value1}
          />
          <span>{t('months')}</span>
        </>
      );
    case ConfigurationCriteriaEnum.SalesDeviationForecast:
      return (
        <>
          <span>{t('The number of sales has a deviation greater than')}</span>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue1(e.target.value);
              onUpdateCriteria('value1', e.target.value, true);
            }}
            value={value1}
          />
          <span>{t('sales compared to forecast data for the last month')}</span>
        </>
      );
    case ConfigurationCriteriaEnum.SmapeLastMonths:
      return (
        <>
          <span>{t('The SMAP of the last')}</span>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue1(e.target.value);
              onUpdateCriteria('value1', e.target.value, true);
            }}
            value={value1}
          />
          <span>{t('months is')}</span>
          <Select
            onValueChange={(value) => onUpdateCriteria('value2', value, false)}
            value={`${value2}`}
          >
            <SelectTrigger className='h-8 w-48' disabled={!criterion.apply}>
              <SelectValue placeholder='' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem disabled={!criterion.apply} value='gt'>
                {t('Greater than')}
              </SelectItem>
              <SelectItem disabled={!criterion.apply} value='lt'>
                {t('Less than')}
              </SelectItem>
              <SelectItem disabled={!criterion.apply} value='ge'>
                {t('Greater than or equal to')}
              </SelectItem>
              <SelectItem disabled={!criterion.apply} value='le'>
                {t('Less than or equal to')}
              </SelectItem>
            </SelectContent>
          </Select>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue3(e.target.value);
              onUpdateCriteria('value3', e.target.value, true);
            }}
            value={value3}
          />
        </>
      );
    case ConfigurationCriteriaEnum.FirstStockingLastMonths:
      return (
        <>
          <span>{t('Date of first stocking in the last')}</span>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue1(e.target.value);
              onUpdateCriteria('value1', e.target.value, true);
            }}
            value={value1}
          />
          <span>{t('last months')}</span>
        </>
      );
    case ConfigurationCriteriaEnum.TrackingSignal:
      return (
        <>
          <span>{t('Tracking signal is equal to -3 or 3 in the last')}</span>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue1(e.target.value);
              onUpdateCriteria('value1', e.target.value, true);
            }}
            value={value1}
          />
          <span>{t('last months')}</span>
        </>
      );
    case ConfigurationCriteriaEnum.DestockingLastDays:
      return (
        <>
          <span>{t('Destocking references not stopped since less than')}</span>
          <Input
            className='size-8 p-1 text-center'
            disabled={!criterion.apply}
            onChange={(e) => {
              setValue1(e.target.value);
              onUpdateCriteria('value1', e.target.value, true);
            }}
            value={value1}
          />
          <span>{t('days')}</span>
        </>
      );
    default:
      return null;
  }
};
